// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Button } from './ui/button';
import {
  Bell,
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { usePendingNotifications } from '../hooks/usePendingNotifications';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const URGENCY_STYLES = {
  info: {
    badge: 'bg-sky-100 dark:bg-sky-950 text-sky-700 dark:text-sky-300',
    icon: 'text-sky-600',
    Icon: Bell,
  },
  warning: {
    badge: 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300',
    icon: 'text-amber-600',
    Icon: AlertCircle,
  },
  overdue: {
    badge: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
    icon: 'text-red-600',
    Icon: AlertCircle,
  },
};

function NotificationItem({ notification, onDismiss, onNavigate }) {
  const style = URGENCY_STYLES[notification.urgency] || URGENCY_STYLES.info;
  const { Icon } = style;

  const openWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(notification.whatsapp_text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="p-3 border-b border-border last:border-0 hover:bg-accent/40 transition-colors"
      data-testid={`notification-item-${notification.id}`}
    >
      <div className="flex items-start gap-2">
        <Icon className={`w-5 h-5 ${style.icon} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-sm font-semibold text-foreground leading-tight">
              {notification.title}
            </p>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${style.badge}`}>
              {notification.urgency === 'overdue' ? 'ATRASADO' : 'PENDENTE'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-2.5">{notification.description}</p>
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              variant="default"
              className="h-7 px-2 text-xs bg-[#25D366] hover:bg-[#20BD5A] text-white"
              onClick={openWhatsApp}
              data-testid={`notification-whatsapp-${notification.id}`}
            >
              <MessageCircle className="w-3 h-3 mr-1" />
              WhatsApp
            </Button>
            {notification.action_url && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={() => onNavigate(notification.action_url)}
                data-testid={`notification-open-${notification.id}`}
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Abrir
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => onDismiss(notification.id)}
              data-testid={`notification-dismiss-${notification.id}`}
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Resolvido
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PendingNotificationsBell() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { notifications, dismiss } = usePendingNotifications(isAdmin());

  if (!isAdmin()) return null;

  const count = notifications.length;
  const hasOverdue = notifications.some((n) => n.urgency === 'overdue');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-xl hover:bg-accent"
          data-testid="pending-notifications-bell"
          title="Notificações pendentes"
        >
          <Bell className={`w-5 h-5 ${count > 0 ? 'text-foreground' : 'text-muted-foreground'}`} />
          {count > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${
                hasOverdue ? 'bg-red-500' : 'bg-amber-500'
              }`}
              data-testid="notifications-count-badge"
            >
              {count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[340px] sm:w-[400px] p-0 max-h-[80vh] overflow-y-auto"
        data-testid="notifications-popover"
      >
        <div className="px-3 py-2.5 border-b border-border bg-muted/30">
          <p className="text-sm font-semibold text-foreground">Notificações</p>
          <p className="text-xs text-muted-foreground">
            {count === 0
              ? 'Nenhuma pendência no momento'
              : `${count} ${count === 1 ? 'pendência' : 'pendências'} aguardando ação`}
          </p>
        </div>
        {count === 0 ? (
          <div className="p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500/40 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Tudo em dia!</p>
          </div>
        ) : (
          <div>
            {notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onDismiss={dismiss}
                onNavigate={(url) => navigate(url)}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
